import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import UserAccountMenu from '../../components/ui/UserAccountMenu';
import MonthCard from './components/MonthCard';
import CalendarNavigation from './components/CalendarNavigation';
import CalendarStats from './components/CalendarStats';
import QuickFilters from './components/QuickFilters';
import Icon from '../../components/AppIcon';

const ContentCalendar = () => {
  const navigate = useNavigate();
  const [currentYear, setCurrentYear] = useState(2026);
  const [expandedMonths, setExpandedMonths] = useState([0]);
  const [activeFilter, setActiveFilter] = useState('all');

  const calendarData = [
    {
      name: 'January',
      theme: 'New Year, New Precision',
      description: `Start the year highlighting surgical precision and patient outcomes. Focus on technology innovation and setting the tone for excellence in 2026.`,
      weeklyPosts: [
        {
          title: 'New Year Resolution: Precision in Every Procedure',
          description: `Share how Videra's adaptable tissue marker technology enables surgeons to achieve unprecedented accuracy in surgical planning and execution.`,
          format: 'Image Post',contentId: 'jan-w1-001'
        },
        {
          title: 'Technology Spotlight: Adaptable Marking System',description: `Showcase the unique features of Videra's tissue marker and how it adapts to different surgical scenarios for optimal patient outcomes.`,
          format: 'Carousel',
          contentId: 'jan-w2-002'
        },
        {
          title: 'Patient Success Story',
          description: `Feature a compelling patient outcome story demonstrating the impact of precise surgical marking on recovery and results.`,
          format: 'Video',
          contentId: 'jan-w3-003'
        },
        {
          title: 'Surgeon Testimonial',
          description: `Share insights from a leading surgeon about their experience with Videra technology and its impact on their practice.`,
          format: 'Quote Post',
          contentId: 'jan-w4-004'
        }
      ]
    },
    {
      name: 'February',
      theme: 'Heart Health & Surgical Excellence',
      description: `Align with American Heart Month to emphasize cardiovascular surgical precision and the role of advanced marking technology in cardiac procedures.`,
      weeklyPosts: [
        {
          title: 'Heart Health Awareness: Precision Matters',
          description: `Discuss the critical importance of surgical precision in cardiovascular procedures and how Videra supports cardiac surgeons.`,
          format: 'Infographic',
          contentId: 'feb-w1-005'
        },
        {
          title: 'Cardiac Surgery Innovation',
          description: `Highlight how adaptable tissue markers enhance accuracy in complex cardiac surgical procedures.`,
          format: 'Image Post',
          contentId: 'feb-w2-006'
        },
        {
          title: 'Surgeon Q&A: Cardiovascular Applications',
          description: `Feature a Q&A with a cardiovascular surgeon discussing their use of Videra technology in heart procedures.`,
          format: 'Article',
          contentId: 'feb-w3-007'
        },
        {
          title: 'Patient Education: Understanding Surgical Marking',
          description: `Educational content explaining how surgical marking technology contributes to better patient outcomes in cardiac surgery.`,
          format: 'Video',
          contentId: 'feb-w4-008'
        }
      ]
    },
    {
      name: 'March',
      theme: 'Women\'s Health & Surgical Precision',
      description: `Focus on Women's History Month and the importance of precision in women's health surgical procedures, including breast and gynecological surgeries.`,
      weeklyPosts: [
        {
          title: 'Women\'s Health: Precision in Breast Surgery',
          description: `Highlight the role of accurate tissue marking in breast conservation surgery and reconstruction procedures.`,
          format: 'Carousel',
          contentId: 'mar-w1-009'
        },
        {
          title: 'Gynecological Surgery Excellence',
          description: `Showcase how Videra technology supports precision in minimally invasive gynecological procedures.`,
          format: 'Image Post',
          contentId: 'mar-w2-010'
        },
        {
          title: 'Female Surgeon Spotlight',
          description: `Feature a leading female surgeon and her experience using Videra technology in women's health procedures.`,
          format: 'Interview',contentId: 'mar-w3-011'
        },
        {
          title: 'Patient Empowerment Through Technology',
          description: `Discuss how advanced surgical technology empowers women to make informed decisions about their healthcare.`,
          format: 'Article',contentId: 'mar-w4-012'
        }
      ]
    },
    {
      name: 'April',theme: 'Spring Into Innovation',
      description: `Emphasize renewal and innovation in surgical technology. Focus on training, education, and the latest advancements in tissue marking.`,
      weeklyPosts: [
        {
          title: 'Innovation in Surgical Technology',
          description: `Explore the latest innovations in adaptable tissue marking and their impact on surgical outcomes.`,
          format: 'Video',contentId: 'apr-w1-013'
        },
        {
          title: 'Surgical Training Excellence',
          description: `Highlight how Videra technology is being integrated into surgical training programs for the next generation of surgeons.`,
          format: 'Image Post',contentId: 'apr-w2-014'
        },
        {
          title: 'Technology Comparison: Why Adaptability Matters',description: `Educational content comparing traditional marking methods with Videra's adaptable technology.`,
          format: 'Infographic',
          contentId: 'apr-w3-015'
        },
        {
          title: 'Surgeon Success Story',
          description: `Feature a surgeon's journey adopting Videra technology and the improvements they've seen in their practice.`,
          format: 'Article',
          contentId: 'apr-w4-016'
        }
      ]
    },
    {
      name: 'May',
      theme: 'Mental Health & Surgical Confidence',
      description: `Align with Mental Health Awareness Month to discuss how surgical precision and reliable technology contribute to surgeon confidence and patient peace of mind.`,
      weeklyPosts: [
        {
          title: 'Surgeon Confidence Through Technology',
          description: `Discuss how reliable surgical tools like Videra contribute to surgeon confidence and reduced procedural stress.`,
          format: 'Article',
          contentId: 'may-w1-017'
        },
        {
          title: 'Patient Peace of Mind',
          description: `Explore how advanced surgical technology provides patients with confidence in their surgical outcomes.`,
          format: 'Image Post',
          contentId: 'may-w2-018'
        },
        {
          title: 'Reducing Surgical Anxiety',
          description: `Educational content on how precision technology helps reduce anxiety for both surgeons and patients.`,
          format: 'Video',
          contentId: 'may-w3-019'
        },
        {
          title: 'Mental Wellness in Surgical Practice',
          description: `Feature insights on maintaining mental wellness in high-stakes surgical environments with reliable technology.`,
          format: 'Interview',
          contentId: 'may-w4-020'
        }
      ]
    },
    {
      name: 'June',
      theme: 'Summer Safety & Surgical Excellence',
      description: `Focus on safety protocols, best practices, and how Videra technology contributes to safer surgical procedures during the busy summer season.`,
      weeklyPosts: [
        {
          title: 'Surgical Safety First',
          description: `Highlight safety protocols and how Videra's precise marking technology contributes to safer surgical outcomes.`,
          format: 'Infographic',contentId: 'jun-w1-021'
        },
        {
          title: 'Best Practices in Tissue Marking',
          description: `Educational content on best practices for using adaptable tissue markers in various surgical procedures.`,
          format: 'Article',contentId: 'jun-w2-022'
        },
        {
          title: 'Summer Surgical Planning',
          description: `Discuss how surgeons can optimize their summer surgical schedules with efficient, reliable technology.`,
          format: 'Image Post',contentId: 'jun-w3-023'
        },
        {
          title: 'Technology Reliability in High-Volume Periods',
          description: `Showcase how Videra technology maintains consistency and reliability during busy surgical periods.`,
          format: 'Video',contentId: 'jun-w4-024'
        }
      ]
    },
    {
      name: 'July',theme: 'Independence & Surgical Autonomy',
      description: `Celebrate surgical independence and autonomy through advanced technology that gives surgeons greater control and flexibility in their procedures.`,
      weeklyPosts: [
        {
          title: 'Surgical Autonomy Through Technology',
          description: `Discuss how adaptable tissue markers give surgeons greater autonomy and flexibility in their surgical approach.`,
          format: 'Article',contentId: 'jul-w1-025'
        },
        {
          title: 'Freedom to Innovate',
          description: `Highlight how Videra technology enables surgeons to innovate and customize their surgical techniques.`,
          format: 'Image Post',contentId: 'jul-w2-026'
        },
        {
          title: 'Independence Day Special: Celebrating Surgical Excellence',
          description: `Feature stories of surgical excellence and independence enabled by advanced marking technology.`,
          format: 'Carousel',contentId: 'jul-w3-027'
        },
        {
          title: 'Surgeon Testimonial: Freedom in Practice',description: `Share a surgeon's perspective on how Videra technology has given them greater freedom in their surgical practice.`,
          format: 'Video',
          contentId: 'jul-w4-028'
        }
      ]
    },
    {
      name: 'August',
      theme: 'Back to School: Education & Training',
      description: `Focus on surgical education, training programs, and how Videra technology is being integrated into medical education for future surgeons.`,
      weeklyPosts: [
        {
          title: 'Surgical Education Excellence',
          description: `Highlight how Videra technology is being used in surgical training programs and medical education.`,
          format: 'Article',
          contentId: 'aug-w1-029'
        },
        {
          title: 'Training the Next Generation',
          description: `Feature stories of surgical residents and fellows learning with Videra's adaptable tissue marking technology.`,
          format: 'Image Post',contentId: 'aug-w2-030'
        },
        {
          title: 'Educational Resources for Surgeons',
          description: `Share educational resources and training materials for surgeons learning to use Videra technology.`,
          format: 'Infographic',contentId: 'aug-w3-031'
        },
        {
          title: 'Continuing Medical Education',
          description: `Discuss the importance of ongoing education and how Videra supports continuous learning in surgical practice.`,
          format: 'Video',contentId: 'aug-w4-032'
        }
      ]
    },
    {
      name: 'September',theme: 'Fall Into Precision',
      description: `Emphasize precision and accuracy as the surgical season ramps up. Focus on case studies and real-world applications of Videra technology.`,
      weeklyPosts: [
        {
          title: 'Precision in Practice: Case Study',
          description: `Share a detailed case study demonstrating the precision and accuracy achieved with Videra tissue markers.`,
          format: 'Article',contentId: 'sep-w1-033'
        },
        {
          title: 'Real-World Applications',
          description: `Showcase diverse real-world applications of Videra technology across different surgical specialties.`,
          format: 'Carousel',contentId: 'sep-w2-034'
        },
        {
          title: 'Surgeon Spotlight: Precision Expert',
          description: `Feature a surgeon known for their precision and their experience with Videra technology.`,
          format: 'Interview',contentId: 'sep-w3-035'
        },
        {
          title: 'Measuring Success: Outcomes Data',
          description: `Present data and outcomes demonstrating the impact of precise tissue marking on surgical results.`,
          format: 'Infographic',contentId: 'sep-w4-036'
        }
      ]
    },
    {
      name: 'October',theme: 'Breast Cancer Awareness & Surgical Precision',
      description: `Align with Breast Cancer Awareness Month to highlight the critical role of precise tissue marking in breast surgery and cancer treatment.`,
      weeklyPosts: [
        {
          title: 'Breast Cancer Awareness: Precision Saves Lives',
          description: `Discuss the critical importance of precise tissue marking in breast cancer surgery and treatment.`,
          format: 'Article',contentId: 'oct-w1-037'
        },
        {
          title: 'Breast Conservation Surgery Excellence',
          description: `Highlight how Videra technology supports breast conservation surgery and optimal cosmetic outcomes.`,
          format: 'Image Post',contentId: 'oct-w2-038'
        },
        {
          title: 'Survivor Stories: Technology That Makes a Difference',
          description: `Feature breast cancer survivor stories and the role of precise surgical technology in their treatment.`,
          format: 'Video',contentId: 'oct-w3-039'
        },
        {
          title: 'Oncoplastic Surgery Innovation',
          description: `Showcase how adaptable tissue markers enhance oncoplastic surgical techniques and patient outcomes.`,
          format: 'Carousel',contentId: 'oct-w4-040'
        }
      ]
    },
    {
      name: 'November',theme: 'Gratitude & Surgical Excellence',
      description: `Express gratitude for surgical teams and healthcare professionals while highlighting the collaborative nature of surgical excellence with Videra technology.`,
      weeklyPosts: [
        {
          title: 'Thankful for Surgical Teams',
          description: `Celebrate surgical teams and the collaborative excellence that Videra technology supports.`,
          format: 'Image Post',contentId: 'nov-w1-041'
        },
        {
          title: 'Healthcare Heroes: Surgeon Appreciation',
          description: `Feature stories of dedicated surgeons and their commitment to patient care with advanced technology.`,
          format: 'Article',contentId: 'nov-w2-042'
        },
        {
          title: 'Team Collaboration in Surgery',
          description: `Highlight how Videra technology facilitates better communication and collaboration in surgical teams.`,
          format: 'Video',contentId: 'nov-w3-043'
        },
        {
          title: 'Year in Review: Surgical Achievements',
          description: `Reflect on surgical achievements and innovations throughout 2026 with Videra technology.`,
          format: 'Infographic',contentId: 'nov-w4-044'
        }
      ]
    },
    {
      name: 'December',theme: 'Year-End Excellence & 2027 Vision',
      description: `Reflect on 2026 achievements and look forward to 2027 innovations. Focus on continued commitment to surgical excellence and patient outcomes.`,
      weeklyPosts: [
        {
          title: '2026 Highlights: A Year of Precision',
          description: `Showcase the major achievements and milestones in surgical precision with Videra technology throughout 2026.`,
          format: 'Carousel',contentId: 'dec-w1-045'
        },
        {
          title: 'Looking Ahead: 2027 Innovations',
          description: `Preview upcoming innovations and enhancements to Videra technology planned for 2027.`,
          format: 'Article',contentId: 'dec-w2-046'
        },
        {
          title: 'Holiday Message: Commitment to Excellence',
          description: `Share a holiday message emphasizing continued commitment to surgical excellence and patient care.`,
          format: 'Image Post',contentId: 'dec-w3-047'
        },
        {
          title: 'New Year Preview: Setting Goals for 2027',
          description: `Encourage surgeons to set goals for 2027 and how Videra technology will support their success.`,
          format: 'Video',contentId: 'dec-w4-048'
        }
      ]
    }
  ];

  const toggleMonth = (index) => {
    setExpandedMonths(prev => 
      prev?.includes(index) 
        ? prev?.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const handleViewContent = (contentId) => {
    navigate('/social-content-library', { state: { contentId } });
  };

  const handleExportPDF = () => {
    console.log('Exporting calendar as PDF for year:', currentYear);
    alert('PDF export functionality will download a formatted calendar document.');
  };

  const handleExportCSV = () => {
    console.log('Exporting calendar as CSV for year:', currentYear);
    alert('CSV export functionality will download calendar data in spreadsheet format.');
  };

  const filteredMonths = useMemo(() => {
    const currentMonth = new Date()?.getMonth();
    
    switch (activeFilter) {
      case 'current':
        return calendarData?.filter((_, index) => index === currentMonth);
      case 'upcoming':
        return calendarData?.filter((_, index) => index >= currentMonth);
      case 'awareness':
        return calendarData?.filter(month => 
          month?.theme?.toLowerCase()?.includes('awareness') || 
          month?.theme?.toLowerCase()?.includes('health')
        );
      default:
        return calendarData;
    }
  }, [activeFilter, calendarData]);

  const stats = useMemo(() => {
    const currentMonth = new Date()?.getMonth();
    const totalPosts = calendarData?.reduce((sum, month) => sum + month?.weeklyPosts?.length, 0);
    const completedMonths = currentMonth;
    const upcomingPosts = calendarData?.slice(currentMonth)?.reduce((sum, month) => sum + month?.weeklyPosts?.length, 0);

    return { totalPosts, completedMonths, upcomingPosts };
  }, [calendarData]);

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <div className="flex-1 lg:ml-60">
        <header className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
          <div className="flex items-center justify-between h-20 px-4 md:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <Icon name="Calendar" size={28} color="var(--color-primary)" />
              <div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">Content Calendar</h1>
                <p className="text-xs md:text-sm text-muted-foreground font-caption mt-1">
                  Plan your year with strategic content themes
                </p>
              </div>
            </div>
            <UserAccountMenu />
          </div>
        </header>

        <main className="p-4 md:p-6 lg:p-8">
          <CalendarNavigation
            currentYear={currentYear}
            onYearChange={setCurrentYear}
            onExportPDF={handleExportPDF}
            onExportCSV={handleExportCSV}
          />

          <CalendarStats
            totalPosts={stats?.totalPosts}
            completedMonths={stats?.completedMonths}
            upcomingPosts={stats?.upcomingPosts}
          />

          <QuickFilters
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
          />

          <div className="space-y-4 md:space-y-6">
            {filteredMonths?.map((month, index) => (
              <MonthCard
                key={index}
                month={month}
                isExpanded={expandedMonths?.includes(index)}
                onToggle={() => toggleMonth(index)}
                onViewContent={handleViewContent}
              />
            ))}
          </div>

          {filteredMonths?.length === 0 && (
            <div className="text-center py-12 md:py-16">
              <Icon name="Calendar" size={64} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg md:text-xl font-medium text-foreground mb-2">No months found</h3>
              <p className="text-sm md:text-base text-muted-foreground">
                Try adjusting your filters to see more content
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ContentCalendar;